import React, { memo, useContext, useMemo } from 'react';
import PropTypes from 'prop-types';
import Select from 'react-select';
import Switch from 'react-switch';
import TimezoneSelect from 'react-timezone-select';

import BrandingContext from '../../Context/BrandingContext';
import { EditAssetCCDisplay } from './EditAssetCCDisplay';
import Can from '../../RBAC/Can';

import { PROVISION_MODEL_ID_SET } from '../../Constants/StaticUUIDs';
import {
  ASSET_COMMANDS_UPDATE,
  ASSET_MODEL,
  ASSET_MODEL_READ,
  REMOTE,
} from '../../Constants/RBAC';

export default memo(AssetBasicInfo);
function AssetBasicInfo({
  availableTemplates,
  commandAndControl,
  currentTemplateID,
  handleCCToggle,
  handlePropUpdate,
  handleTemplateSelect,
  handleTimezoneSelected,
  handleTimezoneToggle,
  nickname,
  orgID,
  selectedTemplate,
  selectedTimezone,
  templatesLoading,
  timezone,
  vendor,
  visibility,
}) {
  const { primary_alert } = useContext(BrandingContext);
  const templateChanged =
    selectedTemplate?.value && selectedTemplate.value !== currentTemplateID;
  const currentIsProvisionModel = useMemo(() => {
    return PROVISION_MODEL_ID_SET.has(currentTemplateID);
  }, [currentTemplateID]);

  return (
    <div className={`${visibility} flex-grow-1 text-muted`}>
      <div className="form-group">
        <label htmlFor="nickname">Name</label>
        <input
          name="nickname"
          type="text"
          className="form-control"
          value={nickname || ''}
          onChange={handlePropUpdate}
        />
      </div>
      <div className="d-flex align-items-center mb-2">
        <Switch
          checked={timezone === 'auto'}
          checkedIcon={false}
          height={15}
          onChange={handleTimezoneToggle}
          onColor={primary_alert}
          uncheckedIcon={false}
          width={30}
        />
        <p className="mb-0 ml-3 modal-supplemental-text">
          Set Timezone Based On GPS
        </p>
      </div>
      <div className="mb-3">
        <TimezoneSelect
          isClearable={false}
          isDisabled={timezone === 'auto'}
          name="timezone"
          onChange={handleTimezoneSelected}
          placeholder="Select Timezone"
          value={selectedTimezone}
        />
      </div>
      {['1'].includes(vendor) ? null : (
        <Can
          orgID={orgID}
          perform={ASSET_COMMANDS_UPDATE}
          resource={REMOTE}
          yes={() => (
            <div className="form-group">
              <EditAssetCCDisplay
                commandAndControl={commandAndControl}
                handleCCToggle={handleCCToggle}
              />
            </div>
          )}
        />
      )}
      {currentIsProvisionModel ? null : (
        <Can
          orgID={orgID}
          perform={ASSET_MODEL_READ}
          resource={ASSET_MODEL}
          yes={() => (
            <div className="form-group">
              <label htmlFor="assetModel">Asset Model</label>
              <span
                id="assetModelHelp"
                className="form-text three-quarter-text mb-3 mt-0"
              >
                Changing the Asset Model will change the collections and signals
                that this asset uses and how the data is interpreted. The
                dashboard will default to the new Asset Model&#39;s layout.
              </span>
              <Select
                aria-describedby="assetModelHelp"
                aria-labelledby="assetModelWarning"
                isDisabled={templatesLoading}
                isLoading={templatesLoading}
                isSearchable
                maxMenuHeight={175}
                name="assetModel"
                onChange={handleTemplateSelect}
                options={availableTemplates}
                value={selectedTemplate}
              />
              <div
                id="assetModelWarning"
                className="mt-2"
                style={{ height: '20px' }}
              >
                <span
                  className={
                    templateChanged
                      ? 'form-text three-quarter-text text-warning mt-0'
                      : 'd-none'
                  }
                >
                  You&#39;ve changed the Asset Model. Asset Model data will be
                  inherited which affects the dashboard, details, alerts, and
                  DM1 Faults.
                </span>
              </div>
            </div>
          )}
        />
      )}
      <style jsx>{`
        .form-control:focus {
          box-shadow: none;
        }
        input {
          color: var(--black-text);
        }
        input::placeholder {
          color: var(--icon-link-gray);
          font-size: 0.9rem;
        }
      `}</style>
    </div>
  );
}

AssetBasicInfo.propTypes = {
  visibility: PropTypes.string,
  nickname: PropTypes.string,
  orgID: PropTypes.string,
  customTag: PropTypes.string,
  handlePropUpdate: PropTypes.func,
  availableTemplates: PropTypes.arrayOf(PropTypes.object),
  templatesLoading: PropTypes.bool,
  selectedTemplate: PropTypes.object,
  handleTemplateSelect: PropTypes.func,
  handleTimezoneSelected: PropTypes.func,
  handleTimezoneToggle: PropTypes.func,
  currentTemplateID: PropTypes.string,
  commandAndControl: PropTypes.bool,
  handleCCToggle: PropTypes.func,
  selectedTimezone: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  timezone: PropTypes.string,
  vendor: PropTypes.string,
};

AssetBasicInfo.defaultProps = { handleTimezoneToggle: () => null };
